import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import _findInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/find';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/es.string.replace.js';
import { number, dateTime } from '@thisisbud/intl-formats';
import _objectSpread from '@babel/runtime-corejs3/helpers/esm/objectSpread2';
import _sliceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/slice';
import 'core-js/modules/web.dom-collections.iterator.js';

const _excluded$1 = ["currencyDisplay", "signDisplay"];
const currencyCodeRegEx = /[a-z]+/i;
/**
 * Format a currency value according to language conventions.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param value - A currency amount to format
 * @param currency - The ISO 4217 currency code in which the value is to be represented
 * @param options - Format options compatible with the `Intl.NumberFormat` constructor
 * @returns A currency value formatted according to language conventions
 * @public
 *
 * @example
 * ```javascript
 * import { formatCurrency } from '@thisisbud/intl-utils'
 *
 * const result = formatCurrency('en-GB', 12.5, 'GBP')
 * // '£12.50'
 * ```
 */
function formatCurrency(locale, value, currency = 'USD', options = number.currency) {
  const {
      currencyDisplay,
      signDisplay
    } = options,
    opts = _objectWithoutProperties(options, _excluded$1);
  const prefersNarrowSymbol = currencyDisplay === 'narrowSymbol';
  opts.currency = currency;
  // @ts-expect-error The property is a valid option but TypeScript is building its type without it
  opts.currencyDisplay = currencyDisplay === 'narrowSymbol' ? 'symbol' : currencyDisplay; // Get around an issue with Safari and other browsers that don't support the `narrowSymbol` value for the `currencyDisplay` option
  opts.style = 'currency';
  let output = new Intl.NumberFormat(locale, opts).format(Math.abs(value));
  output = prefersNarrowSymbol ? output.replace(currencyCodeRegEx, '') : output;
  // Get around an issue with Safari and other browsers that don't support the `signDisplay` option
  if (signDisplay === 'never') {
    return output;
  } else if (signDisplay === 'always') {
    return value < 0 ? `-${output}` : `+${output}`;
  } else if (signDisplay === 'exceptZero') {
    if (value === 0) {
      return output;
    }
    return value < 0 ? `-${output}` : `+${output}`;
  }
  return value < 0 ? `-${output}` : output;
}
/**
 * Get the symbol for a currency code.
 *
 * @param currency - The ISO 4217 currency code
 * @returns The symbol corresponding to the given currency code
 * @public
 *
 * @example
 * ```javascript
 * import { getCurrencySymbol } from '@thisisbud/intl-utils'
 *
 * const result = getCurrencySymbol('GBP')
 * // '£'
 * ```
 */
function getCurrencySymbol(currency) {
  const output = new Intl.NumberFormat('en-GB', {
    currency: currency,
    style: 'currency'
  }).formatToParts(1);
  return _findInstanceProperty(output).call(output, function (part) {
    return part.type === 'currency';
  }).value.replace(currencyCodeRegEx, '');
}
/**
 * Get the human-readable name of a currency.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param currency - The ISO 4217 currency code
 * @param plural - Set to `true` to get the name in the plural form
 * @returns The name of the currency
 * @public
 *
 * @example
 * ```javascript
 * import { getCurrencyName } from '@thisisbud/intl-utils'
 *
 * const result = getCurrencyName('en-GB', 'GBP')
 * // 'British pound'
 * ```
 */
function getCurrencyName(locale, currency, plural = false) {
  const output = new Intl.NumberFormat(locale, {
    currency: currency,
    currencyDisplay: 'name',
    maximumFractionDigits: 0,
    style: 'currency'
  }).formatToParts(plural ? 2 : 1);
  return _findInstanceProperty(output).call(output, function (part) {
    return part.type === 'currency';
  }).value;
}

/**
 * Format a date and time according to language conventions.
 *
 * @remarks By default only a date will be returned, use the options to provide the time format
 * @param locale - A string with a BCP 47 language tag
 * @param value - A date object to format
 * @param options - Format options compatible with the `Intl.DateTimeFormat` constructor
 * @returns A date and time formatted according to language conventions
 * @public
 *
 * @example
 * ```javascript
 * import { formatDateTime } from '@thisisbud/intl-utils'
 *
 * const result = formatDateTime('en-GB', new Date())
 * // '17 Dec 2020'
 * ```
 */
function formatDateTime(locale, value, options = dateTime.default) {
  return new Intl.DateTimeFormat(locale, options).format(value);
}
/**
 * Format a date and time according to language conventions and universal time.
 *
 * @remarks By default only a date will be returned, use the options to provide the time format
 * @param locale - A string with a BCP 47 language tag
 * @param value - A date object to format
 * @param options - Format options compatible with the `Intl.DateTimeFormat` constructor
 * @returns A date and time formatted according to language conventions and universal time
 * @public
 *
 * @example
 * ```javascript
 * import { formatDateTime } from '@thisisbud/intl-utils'
 *
 * const result = formatDateTime('en-GB', new Date())
 * // '17 Dec 2020'
 * ```
 */
function formatUTCDateTime(locale, value, options = dateTime.default) {
  return new Intl.DateTimeFormat(locale, _objectSpread(_objectSpread({}, options), {}, {
    timeZone: 'UTC'
  })).format(value);
}
/**
 * Format a date according to language conventions in the context of the reference date.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param value - A date object to format
 * @param options - Format options compatible with the `Intl.DateTimeFormat` constructor
 * @param reference - A date to use as reference, defaults to the current date
 * @returns A date formatted according to language conventions
 * @public
 *
 * @example
 * ```javascript
 * import { smartFormatDate } from '@thisisbud/intl-utils'
 *
 * // Given the current year is 2022
 * const resultA = smartFormatDate('en-GB', new Date(2020, 11, 17))
 * // '17 Dec 2020'
 *
 * const resultB = smartFormatDate('en-GB', new Date())
 * // '17 Dec'
 * ```
 */
function smartFormatDate(locale, value, options = dateTime.default, reference) {
  const dateOptions = _objectSpread({}, options);
  const refDate = reference !== null && reference !== void 0 ? reference : new Date();
  if (value.getFullYear() === refDate.getFullYear()) {
    dateOptions.year = undefined;
  }
  // Prevent times from being formatted
  dateOptions.hour = undefined;
  dateOptions.minute = undefined;
  dateOptions.second = undefined;
  dateOptions.fractionalSecondDigits = undefined;
  return formatDateTime(locale, value, dateOptions);
}
/**
 * Format a date range according to language conventions and the context of the given dates.
 * Start date elements will be omitted where possible.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param from - A start date object to format
 * @param to - An end date object to format
 * @param options - Format options compatible with the `Intl.DateTimeFormat` constructor
 * @returns An array containing the formatted start and end dates as strings or a single string array if the dates are the same
 * @public
 *
 * ```javascript
 * import { smartFormatDateRange } from '@thisisbud/intl-utils'
 *
 * const output = smartFormatDateRange('en-GB', new Date(2020, 11, 1), new Date(2020, 11, 31))
 * // ['1', '31 Dec 2020']
 * ```
 */
function smartFormatDateRange(locale, from, to, options = dateTime.default) {
  const normalizedOptions = _objectSpread({}, options);
  // Prevent times from being formatted
  normalizedOptions.hour = undefined;
  normalizedOptions.minute = undefined;
  normalizedOptions.second = undefined;
  normalizedOptions.fractionalSecondDigits = undefined;
  const fromOptions = _objectSpread({}, normalizedOptions);
  if (from.getFullYear() === to.getFullYear()) {
    fromOptions.year = undefined;
    if (from.getMonth() === to.getMonth()) {
      fromOptions.month = undefined;
      if (from.getDate() === to.getDate()) {
        return [formatDateTime(locale, to, normalizedOptions)];
      }
    }
  }
  return [formatDateTime(locale, from, fromOptions), formatDateTime(locale, to, normalizedOptions)];
}

const defaultOptions = {
  caseFirst: 'upper',
  usage: 'sort'
};
/**
 * Get a string comparison function for the purposes of sorting.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param options - Format options compatible with the `Intl.CollatorOptions` constructor
 * @returns A function that can be used to compare two strings for sorting
 */
function getCompareFn(locale, options = {}) {
  return new Intl.Collator(locale, _objectSpread(_objectSpread({}, defaultOptions), options)).compare;
}

const fallbackOpts = {
  en: {
    conjunction: 'and',
    disjunction: 'or'
  }
};
/**
 * Format a list of strings according to language conventions.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param list - The list of strings to format
 * @param type - The type of list to format
 * @returns A formatted list according to language conventions
 * @public
 *
 * @example
 * ```javascript
 * import { formatList } from '@thisisbud/intl-utils'
 *
 * const result = formatList('en-GB', ['foo', 'bar'], 'conjunction')
 * // 'foo and bar'
 * ```
 */
function formatList(locale, list, type = 'conjunction') {
  // Avoid creating a list formatter instance or use a fallback if possible
  if (list.length === 0) {
    return '';
  } else if (list.length === 1) {
    return list[0];
  }
  // Handle lack of support for list formatting
  if (typeof Intl.ListFormat === 'undefined') {
    const [lang] = locale.split('-');
    const localeOpts = fallbackOpts[lang];
    if (typeof localeOpts === 'undefined') {
      throw new Error(`Unsupported locale '${locale}' for fallback list formatting.`);
    }
    const firstListItems = _sliceInstanceProperty(list).call(list, 0, -1);
    const lastListItem = list[list.length - 1];
    return `${firstListItems.join(', ')} ${localeOpts[type]} ${lastListItem}`;
  }
  // Use built-in support for list formatting if available
  return new Intl.ListFormat(locale, {
    style: 'long',
    type: type
  }).format(list);
}

const _excluded = ["signDisplay"];
/**
 * Format a number according to language conventions.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param value - A numeric value to format
 * @param options - Format options compatible with the `Intl.NumberFormat` constructor
 * @returns A number formatted according to language conventions
 * @public
 *
 * @example
 * ```javascript
 * import { formatNumber } from '@thisisbud/intl-utils'
 *
 * const result = formatNumber('en-GB', 1200)
 * // '1,200'
 * ```
 */
function formatNumber(locale, value, options = number.default) {
  const {
      signDisplay
    } = options,
    opts = _objectWithoutProperties(options, _excluded);
  const output = new Intl.NumberFormat(locale, _objectSpread(_objectSpread({}, opts), {}, {
    style: 'decimal'
  })).format(Math.abs(value));
  // Get around an issue with Safari and other browsers that don't support the `signDisplay` option
  if (signDisplay === 'never') {
    return output;
  } else if (signDisplay === 'always') {
    return value < 0 ? `-${output}` : `+${output}`;
  } else if (signDisplay === 'exceptZero') {
    if (value === 0) {
      return output;
    }
    return value < 0 ? `-${output}` : `+${output}`;
  }
  return value < 0 ? `-${output}` : output;
}

/**
 * Format a percentage value according to language conventions.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param value - A numeric value to format with `1` being equivalent to 100%
 * @param options - Format options compatible with the `Intl.NumberFormat` constructor
 * @returns A percentage value formatted according to language conventions
 * @public
 *
 * @example
 * ```javascript
 * import { formatPercentage } from '@thisisbud/intl-utils'
 *
 * const result = formatPercentage('en-GB', 0.125)
 * // '12.5%'
 * ```
 */
function formatPercentage(locale, value, options = number.percent) {
  return new Intl.NumberFormat(locale, _objectSpread(_objectSpread({}, options), {}, {
    style: 'percent'
  })).format(value);
}

const pluralRuleFns = {
  /**
   * Get the pluralization category for English.
   *
   * @param n - The number to get a plural rule for
   * @param ordinal - Set to `true` to treat the number as ordinal rather than cardinal
   * @returns The pluralization category of the number
   */
  en(n, ordinal) {
    var _context, _context2;
    const s = String(n).split('.');
    const v0 = !s[1];
    const t0 = Number(s[0]) === n;
    const n10 = t0 && _sliceInstanceProperty(_context = s[0]).call(_context, -1);
    const n100 = t0 && _sliceInstanceProperty(_context2 = s[0]).call(_context2, -2);
    if (ordinal) {
      if (n10 === '1' && n100 !== '11') {
        return 'one';
      } else if (n10 === '2' && n100 !== '12') {
        return 'two';
      } else if (n10 === '3' && n100 !== '13') {
        return 'few';
      }
      return 'other';
    }
    return n === 1 && v0 ? 'one' : 'other';
  }
};
/**
 * Get which plural or ordinal rule to use for locale-aware formatting.
 *
 * @param locale - A string with a BCP 47 language tag
 * @param value - The number to get a plural rule for
 * @param ordinal - Set to `true` to treat the number as ordinal rather than cardinal
 * @returns The pluralization or ordinal category of the number
 * @public
 *
 * @example
 * ```javascript
 * import { getPluralRule } from '@thisisbud/intl-utils'
 *
 * const result = getPluralRule('en-GB', 1)
 * // 'one'
 * ```
 */
function getPluralRule(locale, value, ordinal) {
  var _pluralRuleFns$locale;
  const useOrdinal = ordinal === true;
  if (typeof Intl.PluralRules !== 'undefined') {
    return new Intl.PluralRules(locale, {
      type: useOrdinal ? 'ordinal' : 'cardinal'
    }).select(value);
  }
  // Default to a polyfill if available
  const pluralRuleFn = (_pluralRuleFns$locale = pluralRuleFns[locale]) !== null && _pluralRuleFns$locale !== void 0 ? _pluralRuleFns$locale : pluralRuleFns[locale.split('-')[0]]; // eslint-disable-line @typescript-eslint/no-unnecessary-condition
  if (typeof pluralRuleFn !== 'undefined') {
    return pluralRuleFn(value, useOrdinal);
  }
  // Including polyfills for all languages would cause a lot of bloat, we only support specific languages in use at Bud
  throw new Error(`Unsupported locale: ${locale}`);
}

export { formatCurrency, formatDateTime, formatList, formatNumber, formatPercentage, formatUTCDateTime, getCompareFn, getCurrencyName, getCurrencySymbol, getPluralRule, smartFormatDate, smartFormatDateRange };
